// import node module libraries
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { FormSelect } from 'components/elements/form-select/FormSelect';
import { Link } from 'react-router-dom';
import { Col, Form, Image, Row, Table } from 'react-bootstrap';
import {
	useFilters,
	useGlobalFilter,
	usePagination,
	useTable
} from 'react-table';

// import custom components
import GlobalFilter from 'components/elements/advance-table/GlobalFilter';
import Pagination from 'components/elements/advance-table/Pagination';

// import utility file
import { Avatar } from '../../../elements/bootstrap/Avatar';
import MaintenanceService from '../../services/MaintenanceService';
import PersonnelService from '../../services/PersonnelService';


const MaintenanceListItems = () => {
	const [originalMaintenances, setOriginalMaintenances] = useState([]);
	const [Maintenances, setMaintenances] = useState([]);
	const [personnel, setPersonnel] = useState([]); // Add this line
	const [personnelOptions, setPersonnelOptions] = useState([]);

	useEffect(() => {
		const fetchPersonnelOptions = async () => {
			try {
				const personnels = await PersonnelService.getPersonnelOptions();
				setPersonnelOptions(personnels);
			} catch (error) {
				console.error('Error fetching class options', error);
			}
		};

		fetchPersonnelOptions();
	}, []);

	const PERSONNEL_CHOICES = personnelOptions;

	useEffect(() => {
		Promise.all([
			MaintenanceService.getAllMaintenances(),
			PersonnelService.getAllActivePersonnels()
		]).then(([MaintenancesData, personnelData]) => {
			setOriginalMaintenances(MaintenancesData);
			setMaintenances(MaintenancesData);
			setPersonnel(personnelData);
		});
	}, []);

	const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
		<Link
			to=""
			ref={ref}
			onClick={(e) => {
				e.preventDefault();
				onClick(e);
			}}
			className="btn-icon btn btn-ghost btn-sm rounded-circle"
		>
			{children}
		</Link>
	));

	const formatValue = (value, columnId) => {
		if (typeof value === 'string') {
			if (
				columnId === 'personnel' ||
				(columnId === 'personnel' && value)
			) {
				return (
					value && value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
				);
			} else {
				return value;
			}
		} else if (columnId === 'personnel') {
			return value && value.nom
				? value.nom.toUpperCase()
				: ''; // Return the personnel property
		}
		return value;
	};

	const formatStatutMaintenance = (value) => {
		// Remplacez les underscores par des espaces et mettez la première lettre en majuscule
		const formattedValue = value
			.replace(/_/g, ' ')
			.replace(/\b\w/g, (c) => c.toUpperCase());
		return formattedValue;
	};

	const getPersonnelById = (personnelId) => {
		return personnel.find((item) => item.id === personnelId) ?? {};
	};

	const columns = useMemo(
		() => [
			{ accessor: 'id', Header: 'ID', show: false },
			{
				accessor: 'personnel',
				Header: 'Personnel',
				Cell: ({ value, row }) => {
					return (
						<div className="d-flex align-items-center">
							{value ? (
								<Link to={`/services/maintenance/${row.original.id}`}>
									<h5 className="mb-0 ms-2">{formatValue(value, 'personnel')}</h5>
								</Link>
							) : null}
						</div>
					);
				}
			},
			{
				accessor: 'statut',
				Header: 'Statut',
				Cell: ({ value }) => <div>{formatValue(value, 'statut')}</div>
			},
			{
				accessor: 'date_demande',
				Header: 'Date de demande',
				Cell: ({ value }) => <div>{formatValue(value, 'date_demande')}</div>
			},
			{
				accessor: 'urgence',
				Header: 'Urgence',
				Cell: ({ value }) => <div>{formatStatutMaintenance(value)}</div>
			},
			{
				accessor: 'type_service',
				Header: 'Type de service',
				Cell: ({ value }) => <div>{formatValue(value, 'type_service')}</div>
			}
		],
		[]
	);

	const data = useMemo(() => {
		return Maintenances.map((Maintenance) => ({
			...Maintenance,
			personnel: getPersonnelById(Maintenance.personnel),
		}));
	}, [Maintenances]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		page,
		nextPage,
		previousPage,
		state,
		gotoPage,
		pageCount,
		prepareRow,
		setGlobalFilter
	} = useTable(
		{
			columns,
			data,
			initialState: {
				pageSize: 10,
				hiddenColumns: columns.map((column) => {
					if (column.show === false) return column.accessor || column.id;
					else return false;
				})
			}
		},
		useFilters,
		useGlobalFilter,
		usePagination
	);

	const { pageIndex, globalFilter } = state;

	const getFilterTerm = (event) => {
		let filterTerm = event.target.value;
		if (filterTerm !== '') {
			const newMaintenancesList = originalMaintenances.filter((Maintenance) => {
				return Maintenance.personnel === Number(filterTerm);
			});
			setMaintenances(newMaintenancesList);
		} else {
			setMaintenances(originalMaintenances);
		}
	};

	return (
		<Fragment>
			<div className="overflow-hidden p-3">
				<Row className="justify-content-md-between mb-4 mb-xl-0 ">
					<Col xl={2} lg={4} md={6} xs={12}>
						<GlobalFilter
							filter={globalFilter}
							setFilter={setGlobalFilter}
							placeholder="Rechercher un élève"
						/>
					</Col>
					<Col xxl={2} lg={2} md={6} xs={12}>
						{/* records filtering options */}
						<Form.Control
							as={FormSelect}
							placeholder="Filtrer par salle de personnel"
							options={PERSONNEL_CHOICES}
							onChange={getFilterTerm}
						/>
					</Col>
				</Row>
			</div>

			<Table
				hover
				responsive
				{...getTableProps()}
				className="text-nowrap table-centered"
			>
				<thead className="table-light">
					{headerGroups.map((headerGroup) => (
						<tr {...headerGroup.getHeaderGroupProps()}>
							{headerGroup.headers.map((column) => (
								<th {...column.getHeaderProps()}>{column.render('Header')}</th>
							))}
						</tr>
					))}
				</thead>
				<tbody {...getTableBodyProps()}>
					{page.map((row) => {
						prepareRow(row);
						return (
							<tr {...row.getRowProps()}>
								{row.cells.map((cell) => {
									return (
										<td {...cell.getCellProps()}>{cell.render('Cell')}</td>
									);
								})}
							</tr>
						);
					})}
				</tbody>
			</Table>

			<Pagination
				previousPage={previousPage}
				pageCount={pageCount}
				pageIndex={pageIndex}
				gotoPage={gotoPage}
				nextPage={nextPage}
			/>
		</Fragment>
	);
};

export default MaintenanceListItems;
